/**
 * WatermarkedImage — renders any lesson image through the backend
 * watermarking proxy so it carries the current user's email + timestamp.
 *
 * Because <img> tags can't send Authorization headers, we fetch the
 * proxied image as a Blob and hand the <img> a `blob:` URL. Blob URLs
 * are revoked on unmount to avoid memory leaks.
 *
 * Extra client-side friction:
 *   • `draggable={false}` — blocks drag-out-to-desktop
 *   • Right-click intercepted + `report-attempt` telemetry fired
 *   • Native "Save image as…" still works but downloads the WATERMARKED
 *     blob (not the original asset URL) so any leak is traceable
 */

import React, { useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const reportAttempt = async (payload) => {
  try {
    const token = window.localStorage.getItem('token');
    if (!token) return;
    await axios.post(
      `${API_BASE}/api/content-protection/report-attempt`,
      payload,
      { headers: { Authorization: `Bearer ${token}` } },
    );
  } catch (_) { /* silent */ }
};

const WatermarkedImage = ({
  src,
  alt = '',
  lessonId,
  courseId,
  className = '',
  style,
  onError,
  testId = 'watermarked-image',
  ...rest
}) => {
  const { API } = useContext(AuthContext);
  const [blobUrl, setBlobUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const objectUrlRef = useRef(null);

  useEffect(() => {
    if (!src) return undefined;
    let cancelled = false;
    (async () => {
      setFailed(false);
      try {
        const res = await axios.get(`${API}/content-protection/image`, {
          params: { url: src, lesson_id: lessonId },
          responseType: 'blob',
        });
        if (cancelled) return;
        objectUrlRef.current = URL.createObjectURL(res.data);
        setBlobUrl(objectUrlRef.current);
      } catch (_) {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [API, src, lessonId]);

  // Fall back to a low-friction native <img> if the proxy failed so the
  // lesson never shows a broken picture. Native flavour keeps the
  // right-click block + drag block so it's still deterrent-only.
  const finalSrc = blobUrl || (failed ? src : null);

  const handleContext = (e) => {
    e.preventDefault();
    toast.warning('Saving course images is disabled — see our Terms of Use.');
    reportAttempt({ lesson_id: lessonId, course_id: courseId, kind: 'image_right_click' });
  };

  return (
    <img
      src={finalSrc || undefined}
      alt={alt}
      draggable={false}
      onContextMenu={handleContext}
      onDragStart={(e) => e.preventDefault()}
      onError={onError}
      className={className}
      style={{ userSelect: 'none', WebkitUserSelect: 'none', ...style }}
      data-testid={testId}
      {...rest}
    />
  );
};

export default WatermarkedImage;
